import type { IconType } from '@evisort/eds/types/SharedIconProps';

import { Icon } from '@evisort/eds/components/Icon/Icon';
import { StatusBadge } from '@evisort/eds/components/StatusBadge/StatusBadge';
import { useCallback, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import brandLockupSrc from '~/assets/images/logo-evisort-dark.webp';
import { testIsActiveLink } from '~/eds/src/modules/PageHeader/utils';
import { NavLink } from '~/eds/src/types/NavLink';
import { getSidebarAnalyticsItem } from '~/features/app/sidebarAnalytics';
import { useSidebarNav } from '~/features/app/SidebarNavContext';
import {
  ASK_AI_PATHNAME,
  FEEDBACK_PATHNAME,
  SidebarNavControl,
} from '~/features/app/SidebarNavControl';
import { SidebarNavFlyout } from '~/features/app/SidebarNavFlyout';
import { useNavigationItems } from '~/features/app/useNavigationItems';
import { useAskAnything } from '~/features/ask-anything/ask-anything-button/useAskAnything';
import { useCandu } from '~/features/onboarding/Candu/useCandu';
import { CLMEvolutionFeedbackModal } from '~/features/workflows-clm-evolution/feedback/CLMEvolutionFeedbackModal';
import { messages as feedbackMessages } from '~/features/workflows-clm-evolution/feedback/messages';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { slices } from '~/redux/slices';
import search from '~/redux/slices/search';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';

import './AppSidebarNav.css';

// ─── i18n ─────────────────────────────────────────────────────────────────────

const messages = defineMessages({
  expandSection: {
    id: 'AppSidebarNav.expandSection',
    defaultMessage: 'Expand {label} section',
    description:
      'Accessible label for the chevron that expands a sidebar nav section.',
  },
  collapseSection: {
    id: 'AppSidebarNav.collapseSection',
    defaultMessage: 'Collapse {label} section',
    description:
      'Accessible label for the chevron that collapses a sidebar nav section.',
  },
  sidebarLandmark: {
    id: 'AppSidebarNav.sidebarLandmark',
    defaultMessage: 'sidebar navigation',
    description: 'Accessible name for the sidebar navigation landmark.',
  },
  mainNavLandmark: {
    id: 'AppSidebarNav.mainNavLandmark',
    defaultMessage: 'main navigation',
    description: 'Accessible name for the primary nav list inside the sidebar.',
  },
  goHome: {
    id: 'AppSidebarNav.goHome',
    defaultMessage: 'Go to home',
    description:
      'Accessible label for the logo button that navigates to the home page.',
  },
  workspaceLabel: {
    id: 'AppSidebarNav.workspaceLabel',
    defaultMessage: 'Workspace',
    description: 'Overline label above the sidebar nav sections.',
  },
  showSidebar: {
    id: 'AppSidebarNav.showSidebar',
    defaultMessage: 'Show sidebar',
    description:
      'Tooltip and a11y label for the sidebar toggle when the sidebar is collapsed.',
  },
  hideSidebar: {
    id: 'AppSidebarNav.hideSidebar',
    defaultMessage: 'Hide sidebar',
    description:
      'Tooltip and a11y label for the sidebar toggle when the sidebar is expanded.',
  },
  askAi: {
    id: 'AppSidebarNav.askAi',
    defaultMessage: 'Ask AI',
    description: 'Label for the sidebar entry that opens the AI chatbot.',
  },
});

// ─── Icon mapping ─────────────────────────────────────────────────────────────

function getNavIcon(pathname: string): IconType {
  const p = pathname.toLowerCase();
  switch (true) {
    case p.startsWith('/automation'):
      return 'ai-custom-automation';
    case p.startsWith('/admin'):
      return 'settings-gear';
    case p.startsWith('/alerts'):
      return 'alert';
    case p.startsWith('/analyzer'):
      return 'search';
    case p.startsWith('/clause-library'):
      return 'library';
    case p.startsWith('/dashboards') || p.startsWith('/dashboard'):
      return 'dashboard';
    case p.startsWith('/document'):
      return 'document';
    case p.startsWith('/guidelines'):
      return 'library';
    case p.startsWith('/managed-entities') || p.startsWith('/parties'):
      return 'work-briefcase';
    case p.startsWith('/playbooks'):
      return 'library';
    case p.startsWith('/search'):
      return 'search';
    case p.startsWith('/saved-searches'):
      return 'bookmark';
    case p.startsWith('/workflow/groups') || p.startsWith('/document/groups'):
      return 'group';
    case p.startsWith('/workflow'):
      return 'workflow';
    default:
      return 'document';
  }
}

// Behind FlagType.SidebarNav: swaps in the dedicated nav-only icon family
// (packages/eds `nav-*` entries) for the primary-nav rows it covers, leaving
// every other icon (Ask AI, Feedback, panel toggle) on the current EDS set.
const EXPLORATION_NAV_ICON: Partial<Record<IconType, IconType>> = {
  home: 'nav-home',
  document: 'nav-documents',
  workflow: 'nav-workflow',
  dashboard: 'nav-dashboard',
  'ai-custom-automation': 'nav-automation',
  library: 'nav-library',
  search: 'nav-search',
  'settings-gear': 'nav-admin',
};

// The current chevron hard-swaps between two glyphs on toggle (no transition
// is possible between two different icons). Keep the same resting-down/
// expanded-up direction, but get there by rotating a single chevron-down
// 180deg via CSS instead — the transition on .sidebar-chevron-btn
// .eds-icon--chevron-down (AppSidebarNav.css) animates it.
function resolveChevronIcon(
  hasSidebarNavUxExploration: boolean,
  isExpanded: boolean,
): IconType {
  if (hasSidebarNavUxExploration) return 'chevron-down';
  return isExpanded ? 'caret-up' : 'caret-down';
}

// ─── Sub-item component ───────────────────────────────────────────────────────

interface SidebarSubItemProps {
  link: NavLink;
  activePathname: string;
  onActivate: (path: string) => void;
}

const SidebarSubItem = ({
  link,
  activePathname,
  onActivate,
}: SidebarSubItemProps) => {
  const isActive = testIsActiveLink(activePathname)(link as NavLink);

  return (
    <SidebarNavControl
      analyticsItem={getSidebarAnalyticsItem(link.pathname)}
      className="sidebar-sub-item-btn"
      isActive={isActive}
      pathname={link.pathname}
      onActivate={onActivate}
    >
      <span className="sidebar-sub-item-label">{link.label}</span>
      {link.badge && (
        <span className="sidebar-nav-item-badge">
          <StatusBadge status="informational" text={link.badge} />
        </span>
      )}
    </SidebarNavControl>
  );
};

// ─── Nav item component ───────────────────────────────────────────────────────

interface SidebarNavItemProps {
  link: NavLink;
  isCollapsed: boolean;
  activePathname: string;
  onActivate: (path: string) => void;
  /** Overrides the icon derived from the link's pathname (used for entries
   *  like Home / Ask AI that aren't part of the pathname → icon mapping). */
  icon?: IconType;
  analyticsSection?: string;
  testId?: string;
}

const SidebarNavItem = ({
  link,
  isCollapsed,
  activePathname,
  onActivate,
  icon: iconOverride,
  analyticsSection,
  testId,
}: SidebarNavItemProps) => {
  const intl = useIntl();
  const isActive = testIsActiveLink(activePathname)(link);
  const anyChildActive = Boolean(
    link.children?.some(testIsActiveLink(activePathname)),
  );
  const isOwnLinkActive = isActive && !anyChildActive;
  const hasChildren = Boolean(link.children?.length);
  const hasSidebarNavUxExploration = useFlag(FlagType.SidebarNav);
  const baseIcon = iconOverride ?? getNavIcon(link.pathname);
  const explorationIcon = hasSidebarNavUxExploration
    ? EXPLORATION_NAV_ICON[baseIcon]
    : undefined;
  const icon = explorationIcon ?? baseIcon;
  const isIconFilled = explorationIcon !== undefined && isActive;
  const iconEl = (
    <Icon
      filled={isIconFilled}
      icon={icon}
      size={hasSidebarNavUxExploration ? 's' : undefined}
    />
  );
  // Ask AI's icon is a full-color brand mark with no background of its own —
  // give it the same gradient tile AskAnythingButton's cta-ai variant uses,
  // instead of floating bare against the row like the outline glyphs.
  const isAskAi = link.pathname === ASK_AI_PATHNAME;
  const iconContent =
    hasSidebarNavUxExploration && isAskAi ? (
      <span className="sidebar-nav-ask-ai-icon-tile">{iconEl}</span>
    ) : (
      iconEl
    );

  const [isExpanded, setIsExpanded] = useState(false);

  // Auto-expand when active section changes
  useEffect(() => {
    if (isActive && hasChildren) {
      setIsExpanded(true);
    }
  }, [isActive, hasChildren]);

  if (isCollapsed) {
    const collapsedItem = (
      <SidebarNavControl
        analyticsItem={getSidebarAnalyticsItem(link.pathname)}
        analyticsSection={analyticsSection}
        className="sidebar-nav-collapsed-item"
        isActive={isActive}
        pathname={link.pathname}
        testId={testId}
        onActivate={onActivate}
      >
        <span aria-hidden="true" className="sidebar-nav-collapsed-icon">
          {iconContent}
        </span>
        <span className="sidebar-nav-collapsed-label">{link.label}</span>
      </SidebarNavControl>
    );

    // The rail has no room to expand a section inline, so its children live in
    // a hover/focus flyout instead of being dropped.
    if (!hasChildren) {
      return collapsedItem;
    }

    return (
      <SidebarNavFlyout
        activePathname={activePathname}
        label={link.label}
        links={link.children!}
        onActivate={onActivate}
      >
        {collapsedItem}
      </SidebarNavFlyout>
    );
  }

  // A group with sub-items becomes its own section (named after the parent
  // item) so the parent and its children all report that section instead of
  // the ambient one — the wrapper's attribute is only present when grouped,
  // so ungrouped items keep inheriting the ambient section unchanged.
  const groupSection = hasChildren
    ? getSidebarAnalyticsItem(link.pathname)
    : undefined;

  return (
    <div
      className="sidebar-nav-item-wrap"
      data-amp-track-section={groupSection}
    >
      <div
        className={`sidebar-nav-row${isOwnLinkActive ? ' sidebar-nav-row--active' : ''}`}
      >
        {/* Icon + label: navigates */}
        <SidebarNavControl
          analyticsItem={getSidebarAnalyticsItem(link.pathname)}
          analyticsSection={hasChildren ? undefined : analyticsSection}
          className={`sidebar-nav-item-btn${isActive ? ' sidebar-nav-item-btn--active' : ''}`}
          isActive={isOwnLinkActive}
          pathname={link.pathname}
          testId={testId}
          onActivate={onActivate}
        >
          {iconContent}
          <span className="sidebar-nav-item-label">{link.label}</span>
          {link.badge && (
            <span className="sidebar-nav-item-badge">
              <StatusBadge status="informational" text={link.badge} />
            </span>
          )}
        </SidebarNavControl>

        {/* Chevron: toggles sub-items */}
        {hasChildren && (
          <button
            type="button"
            aria-label={intl.formatMessage(
              isExpanded ? messages.collapseSection : messages.expandSection,
              { label: link.label },
            )}
            aria-expanded={isExpanded}
            className="sidebar-chevron-btn"
            data-amp-track-action="toggle-section"
            data-amp-track-item={getSidebarAnalyticsItem(link.pathname)}
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded((prev: boolean) => !prev);
            }}
          >
            <Icon
              icon={resolveChevronIcon(hasSidebarNavUxExploration, isExpanded)}
              size="s"
            />
          </button>
        )}
      </div>

      {/* Sub-items with hierarchy line */}
      {hasChildren && isExpanded && (
        <div className="sidebar-nav-children-wrap">
          {/* Vertical line anchored to the icon centre (8px left padding + ~8px half-icon) */}
          <div aria-hidden="true" className="sidebar-nav-children-line" />
          {link.children!.map((child) => (
            <SidebarSubItem
              key={child.pathname}
              link={child}
              activePathname={activePathname}
              onActivate={onActivate}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Ask AI nav item ────────────────────────────────────────────────────────────

interface SidebarAskAiLinkProps {
  isCollapsed: boolean;
  activePathname: string;
  label: string;
}

// Only rendered when the Ask AI entry is actually shown, so useAskAnything()
// (and the setPanelZIndex etc. side effects inside useResolveChatBotEntity)
// don't run for users who don't have access to it.
const SidebarAskAiLink = ({
  isCollapsed,
  activePathname,
  label,
}: SidebarAskAiLinkProps) => {
  const { handleToggleChatbot } = useAskAnything();
  const { isMobile, toggleCollapsed } = useSidebarNav();

  const onActivate = useCallback(() => {
    handleToggleChatbot();
    // Mirrors onNavLinkActivate: close the mobile drawer after acting on a
    // nav entry, same as every other sidebar link.
    if (isMobile) toggleCollapsed();
  }, [handleToggleChatbot, isMobile, toggleCollapsed]);

  return (
    <SidebarNavItem
      link={{ label, pathname: ASK_AI_PATHNAME }}
      isCollapsed={isCollapsed}
      activePathname={activePathname}
      onActivate={onActivate}
      icon="evisort-ai-static"
      testId="sidebar-nav-ask-ai"
    />
  );
};

// ─── Feedback nav item ────────────────────────────────────────────────────────

interface SidebarFeedbackLinkProps {
  isCollapsed: boolean;
  activePathname: string;
}

function SidebarFeedbackLink({
  isCollapsed,
  activePathname,
}: SidebarFeedbackLinkProps) {
  const intl = useIntl();
  const [isModalVisible, setIsModalVisible] = useState(false);

  return (
    <>
      <SidebarNavItem
        analyticsSection="footer"
        link={{
          label: intl.formatMessage(feedbackMessages.open),
          pathname: FEEDBACK_PATHNAME,
        }}
        isCollapsed={isCollapsed}
        activePathname={activePathname}
        onActivate={() => setIsModalVisible(true)}
        icon="comment"
        testId="sidebar-nav-feedback"
      />
      <CLMEvolutionFeedbackModal
        isVisible={isModalVisible}
        onHide={() => setIsModalVisible(false)}
      />
    </>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export const AppSidebarNav = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const user = useCurrentUser();
  const { location } = useRouting();
  const { pathname } = location;

  const { isCollapsed, isMobile, toggleCollapsed } = useSidebarNav();
  const hasClmEvolutionPhase1 = useFlag(FlagType.CLMEvolutionPhase1);

  useCandu(user);

  // Side effects only: `SidebarNavControl` renders a `<Link>`, which navigates
  // itself. Runs only for clicks that navigate this tab.
  const onNavLinkActivate = useCallback(() => {
    dispatch(search.actions.reset());
    dispatch(slices.dashboardV2.actions.reset());
    dispatch(slices.searchV3.actions.reset());
    if (isMobile) toggleCollapsed();
  }, [dispatch, isMobile, toggleCollapsed]);

  const { items, homeLink, adminLink, showAskAi } = useNavigationItems({
    onNavLinkClick: onNavLinkActivate,
  });

  // ─── Skip login routes ─────────────────────────────────────────────────────
  if (
    [
      RoutePathType.Login,
      RoutePathType.LoginEmbed,
      RoutePathType.LoginEmbedSuccess,
    ].includes(location.pathname as RoutePathType)
  ) {
    return null;
  }

  return (
    <>
      {/* Backdrop — tapping it closes the sidebar on mobile */}
      {isMobile && !isCollapsed && (
        <div
          aria-hidden="true"
          className="sidebar-backdrop"
          onClick={toggleCollapsed}
        />
      )}
      <aside
        aria-label={intl.formatMessage(messages.sidebarLandmark)}
        aria-hidden={isMobile && isCollapsed ? true : undefined}
        className={`sidebar-aside${isCollapsed ? ' sidebar-aside--collapsed' : ''}${isMobile ? ' sidebar-aside--mobile' : ''}`}
        data-amp-track-region="nav"
        data-amp-track-variant={isCollapsed ? 'collapsed' : 'open'}
      >
        {isMobile && (
          <div
            className="sidebar-mobile-header"
            data-amp-track-section="header"
            data-testid="sidebar-mobile-header"
          >
            <SidebarNavControl
              analyticsItem="logo-home-link"
              ariaLabel={intl.formatMessage(messages.goHome)}
              className="sidebar-mobile-brand-btn"
              isActive={false}
              pathname={RoutePathType.Home}
              testId="sidebar-mobile-brand-button"
              onActivate={onNavLinkActivate}
            >
              <img
                alt="Evisort"
                className="sidebar-mobile-brand-lockup"
                decoding="sync"
                fetchPriority="high"
                height={128}
                loading="eager"
                src={brandLockupSrc}
                width={497}
              />
            </SidebarNavControl>
          </div>
        )}

        {/* ── Workspace header ────────────────────────────────────────────── */}
        <div
          className={`sidebar-workspace-header${isCollapsed ? ' sidebar-workspace-header--collapsed' : ''}`}
          data-amp-track-section="sidebar"
          data-testid="sidebar-workspace-header"
        >
          {!isCollapsed && (
            <span
              className="sidebar-overline"
              data-testid="sidebar-workspace-label"
            >
              {intl.formatMessage(messages.workspaceLabel)}
            </span>
          )}
          <button
            type="button"
            className="sidebar-panel-toggle"
            aria-label={intl.formatMessage(
              isCollapsed ? messages.showSidebar : messages.hideSidebar,
            )}
            title={intl.formatMessage(
              isCollapsed ? messages.showSidebar : messages.hideSidebar,
            )}
            data-amp-track-item="sidebar-visibility-toggle"
            data-testid="sidebar-panel-toggle"
            onClick={toggleCollapsed}
          >
            <Icon
              icon={isCollapsed ? 'left-panel-open' : 'left-panel-close'}
              size="s"
            />
          </button>
        </div>

        {/* ── Nav items ───────────────────────────────────────────────────── */}
        <nav
          aria-label={intl.formatMessage(messages.mainNavLandmark)}
          className={`sidebar-nav-scroll${isCollapsed ? ' sidebar-nav-scroll--collapsed' : ''}`}
          data-amp-track-section="primary-nav"
        >
          {isCollapsed ? (
            <div
              aria-hidden="true"
              className="sidebar-section-divider sidebar-section-divider--collapsed"
              data-testid="sidebar-section-divider-top"
            />
          ) : null}
          {homeLink && (
            <SidebarNavItem
              link={homeLink}
              isCollapsed={isCollapsed}
              activePathname={pathname}
              onActivate={onNavLinkActivate}
              icon="home"
              testId="sidebar-nav-home"
            />
          )}
          {showAskAi && (
            <SidebarAskAiLink
              isCollapsed={isCollapsed}
              activePathname={pathname}
              label={intl.formatMessage(messages.askAi)}
            />
          )}
          {/* Divider — sets Home / Ask AI apart as their own top-level section,
              distinct from the Workspace links below. */}
          <div
            aria-hidden="true"
            className={`sidebar-section-divider${isCollapsed ? ' sidebar-section-divider--collapsed' : ''}`}
            data-testid="sidebar-section-divider"
          />
          {items.map((link) => (
            <SidebarNavItem
              key={link.pathname}
              link={link}
              isCollapsed={isCollapsed}
              activePathname={pathname}
              onActivate={onNavLinkActivate}
            />
          ))}
        </nav>
        <div
          className={`sidebar-nav-footer${isCollapsed ? ' sidebar-nav-footer--collapsed' : ''}`}
        >
          {hasClmEvolutionPhase1 && (
            <SidebarFeedbackLink
              isCollapsed={isCollapsed}
              activePathname={pathname}
            />
          )}

          {adminLink && (
            <SidebarNavItem
              analyticsSection="footer"
              link={adminLink}
              isCollapsed={isCollapsed}
              activePathname={pathname}
              onActivate={onNavLinkActivate}
              testId="sidebar-nav-admin"
            />
          )}
        </div>
      </aside>
    </>
  );
};
